'use client';

import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Drawer } from "./Drawer";
import { useIndividualsCtx, errText } from "../internals";
import { useToast } from "./Toaster";

const blank = { name: "", phone: "", email: "", city: "", status: "active", notes: "" };

export const IndividualDrawer = ({ open, individual, onClose, onChanged }) => {
  const { storage, currentUser } = useIndividualsCtx();
  const toast = useToast();
  const isEdit = !!individual;
  const canDelete = ["owner", "admin"].includes(currentUser.role);

  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(
        individual
          ? {
              name: individual.name || "", phone: individual.phone || "", email: individual.email || "",
              city: individual.city || "", status: individual.status || "active", notes: individual.notes || "",
            }
          : blank
      );
      setErr(null);
      setConfirmDelete(false);
    }
  }, [open, individual]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return setErr("Please enter the customer's name.");
    if (form.phone.length !== 10) return setErr("Please enter a 10-digit mobile number.");
    setErr(null);
    setSaving(true);
    try {
      if (isEdit) await storage.updateIndividual(individual.id, form);
      else await storage.createIndividual(form);
      toast.show(isEdit ? "Customer updated." : "Customer added.", "success");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      const m = errText(e);
      setErr(m);
      toast.show(m, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await storage.deleteIndividual(individual.id);
      toast.show("Customer removed.", "success");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.show(errText(e), "error");
      setDeleting(false);
    }
  };

  const inputCls = "w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-900";

  return (
    <Drawer
      open={open}
      onClose={() => !saving && !deleting && onClose()}
      testId="individual-drawer"
      title={isEdit ? form.name || "Customer" : "Add a customer"}
      subtitle={isEdit ? "Edit this person's details" : "A person you've won"}
      footer={
        <div className="flex gap-2">
          <button type="button" data-testid="individual-cancel-btn" onClick={() => !saving && onClose()} disabled={saving}
            className="flex-1 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            Cancel
          </button>
          <button type="button" data-testid="individual-save-btn" onClick={save} disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60">
            {saving && <Loader2 size={15} className="animate-spin" />}
            {isEdit ? "Save changes" : "Add customer"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Name</label>
          <input data-testid="individual-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} autoFocus placeholder="e.g. Sita Devi" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Mobile number</label>
          <div className="flex items-center rounded-md border border-zinc-300 focus-within:border-zinc-900">
            <span className="px-3 text-sm text-zinc-500">+91</span>
            <input data-testid="individual-phone-input" value={form.phone} onChange={(e) => set("phone")(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="10-digit number" className="w-full rounded-r-md px-1 py-2.5 text-sm outline-none" />
          </div>
          <p className="mt-1 text-xs text-zinc-400">One record per number — repeat enquiries won't duplicate.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">City</label>
            <input data-testid="individual-city-input" value={form.city} onChange={(e) => set("city")(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">Email</label>
            <input data-testid="individual-email-input" value={form.email} onChange={(e) => set("email")(e.target.value)} type="email" placeholder="name@email.com" className={inputCls} />
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-700">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {[{ v: "active", l: "Active" }, { v: "inactive", l: "Inactive" }].map((s) => (
              <button key={s.v} type="button" data-testid={`individual-status-${s.v}`} onClick={() => set("status")(s.v)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  form.status === s.v ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                }`}>
                {s.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Notes <span className="font-normal text-zinc-400">(optional)</span></label>
          <textarea data-testid="individual-notes-input" value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} placeholder="Anything worth remembering" className={inputCls} />
        </div>

        {err && <div data-testid="individual-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {isEdit && canDelete && (
          <div className="border-t border-zinc-200 pt-4">
            {!confirmDelete ? (
              <button type="button" data-testid="individual-delete-btn" onClick={() => setConfirmDelete(true)}
                className="flex w-full items-center gap-2 rounded-md border border-red-200 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50">
                <Trash2 size={15} /> Remove this customer
              </button>
            ) : (
              <div className="rounded-md bg-red-50 px-3 py-3">
                <p className="text-sm text-red-700">Remove {form.name}? This can't be undone.</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting}
                    className="flex-1 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
                    Cancel
                  </button>
                  <button type="button" data-testid="individual-delete-confirm-btn" onClick={doDelete} disabled={deleting}
                    className="flex flex-1 items-center justify-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60">
                    {deleting && <Loader2 size={14} className="animate-spin" />} Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};
